import React, {
    ReactElement,
    useRef,
    useState,
    useContext,
    useEffect,
} from "react";

import { FlyoutTrapContext } from "components/ContactFlyout/ContactFlyout";
import Icon from "components/Icon/Icon";
import { useTabTrap } from "hooks";

import {
    Container,
    StepButton,
    Content,
    StyledForm,
} from "./ContactFlyoutForm.styled";
import ContactFlyoutFormProps from "./ContactFlyoutFormProps";

const ContactFlyoutForm = ({
    heading,
    icon,
    form,
}: ContactFlyoutFormProps): ReactElement => {
    const [showStep, setShowStep] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const stepButtonRef = useRef<HTMLButtonElement>(null);
    const { setIsStepActive, open, setStepHeight } =
        useContext(FlyoutTrapContext);

    useTabTrap(containerRef, showStep);

    useEffect(() => {
        setIsStepActive(showStep);
    }, [showStep, setIsStepActive]);

    useEffect(() => {
        if (!open) {
            setShowStep(false); // Reset step only on close
        }
    }, [open]);

    useEffect(() => {
        if (showStep && contentRef.current && stepButtonRef.current) {
            const height =
                contentRef.current.offsetHeight +
                stepButtonRef.current.offsetHeight;
            setStepHeight(height);
        } else {
            setStepHeight(undefined);
        }
    }, [showStep, setStepHeight]);

    return (
        <Container $isOpen={showStep} ref={containerRef}>
            <StepButton
                $isOpen={showStep}
                actionTheme="black"
                icon={showStep ? "bigArrowLeft28" : icon}
                aria-expanded={showStep}
                onClick={() => setShowStep(!showStep)}
                ref={stepButtonRef}
                gtmClickType="contact-flyout-cta"
            >
                {heading}
                {!showStep && <Icon icon="arrowRight28" />}
            </StepButton>
            {form && (
                <Content
                    ref={contentRef}
                    aria-hidden={!showStep}
                    aria-live="polite"
                    style={{ display: showStep ? "block" : "none" }}
                >
                    {form?.formElements && (
                        <StyledForm
                            action={form.postUrl}
                            items={form.formElements}
                            size={"small"}
                            identifier={`contact-flyout-form-${form.identifier}`}
                            areaName="ContactFlyoutForm"
                        />
                    )}
                </Content>
            )}
        </Container>
    );
};

export default React.memo(ContactFlyoutForm);
